import {Component, HostBinding, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';
import {AuthenticationService} from '../authentication/services/authentication.service';
import {JWTUser} from '@models/jwt-data.model';
import {Log} from 'ng2-logger';
import {Observable} from 'rxjs';
import {Plant} from '@models/plant.model';
import {PlantService} from '@services/plant.service';
import {PagedResponse} from '@models/response.model';
import {UserService} from '@services/user.service';
import {Location} from '@angular/common';
import {NotificationService} from '@services/notification.service';
import {Subscription} from 'rxjs';
import {ResolutionService} from "@services/resolution.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {MsalService} from '@azure/msal-angular';
import {environment} from '../../environments/environment';
import {finalize} from 'rxjs/operators';
import {OperationTypeService} from '@services/operation-type.service';
import {OperationType} from '@app/tool/model/operationType.model';
import {AuthenticationType} from '@app/shared/models/enums/authentication-type.enum';
import {LogoutModalComponent} from '@app/shared/components';
import {InactivityService} from '@app/core/services/inactivity.service';
import {NavigationService} from '@app/core/services/navigation.service';

interface AssignedRoleOption {
    id: number;
    name: string;
    label: string;
}

@UntilDestroy()
@Component({
    selector: 'app-core',
    templateUrl: './core.component.html',
    styleUrls: ['./core.component.scss']
})
export class CoreComponent implements OnInit, OnDestroy {
    _logger = Log.create(CoreComponent.name);
    @ViewChild(LogoutModalComponent, {static: true}) inactivityLogoutModal: LogoutModalComponent;

    readonly badgeLogoutCountdownSeconds = environment.badgeLogoutCountdownSeconds;


    currentUser: JWTUser;
    languageKey = 'user-language';
    noSidebarRoles = [
        'ATOM_Transport'
    ];
    plantDropdownRoles = [
        'ATOM_Operator',
        'ATOM_Storage',
        'ATOM_Service',
        'ATOM_Transport',
    ];
    plantDropdownPages = [
        '/tool-returns-lines',
        '/tool-returns-facilities',
        '/tool-requests-lines',
        '/tool-requests-facilities',
        '/incoming-tool-requests/lines',
        '/incoming-tool-requests/facilities',
        '/incoming-tools-returned/lines',
        '/incoming-tools-returned/facilities',
        '/service-tickets',
        '/transportation-requests'
    ];
    hideHeaderShadow = false;

    screenWidth: number;
    screenHeight: number;
    isMobile = false;

    allPlants$: Observable<PagedResponse<Plant>>;
    selectedPlants$: Observable<number[]>;
    selectedPlantsSubscription: Subscription;
    availableRoles: AssignedRoleOption[] = [];
    selectedRoleId: number;
    isRoleSwitchInProgress = false;
    serviceOperations: OperationType[] = [];
    hideSidebarForSelection = false;
    hideToolbarForSelection = false;
    isSelectionPage = false;

    // Medieval
    selectedPlants: number[];

    get languages() {
        return this.translateService.getLangs();
    }

    get currentLanguage() {
        return this.translateService.currentLang;
    }

    get hasSidebar() {
        return !!this.currentUser &&
            !this.noSidebarRoles.find(role => role === this.currentUser.role) && !this.isMobileEnabled && !this.hideSidebarForSelection;
    }

    get showToolbar() {
        return !this.hideToolbarForSelection;
    }

    get isMobileEnabled() {
        return !!this.currentUser && this.currentUser.role === 'ATOM_Storage' && this.isMobile;
    }

    @HostBinding('class.no-sidebar') get noSidebar() {
        return !this.hasSidebar;
    }

    @HostBinding('class.selection-mode') get selectionMode() {
        return this.isSelectionPage;
    }

    get hasPlantDropdown() {
        if (!this.currentUser) {
            return false;
        }

        const userRole = this.plantDropdownRoles.find(role => role === this.currentUser.role);
        const currentPage = this.location.path();
        const pageAllow = this.plantDropdownPages.find(page => currentPage.includes(page));
        return !!userRole && !!pageAllow;
    }

    constructor(private router: Router,
                public translateService: TranslateService,
                private authService: AuthenticationService,
                private plantService: PlantService,
                private userService: UserService,
                private notificationService: NotificationService,
                private operationTypeService: OperationTypeService,
                private inactivityService: InactivityService,
                private resolutionService: ResolutionService,
                private location: Location,
                private navigationService: NavigationService,
                @Optional() private msalService: MsalService) {

        this.resolutionService.screenSize$.pipe(untilDestroyed(this)).subscribe(screen => {
            this.screenWidth = screen.width;
            this.screenHeight = screen.height;
            this.isMobile = screen.mobile;
        })
    }

    ngOnInit() {
        this.reloadCurrentUserState();
        this.authService.userChanged$.pipe(untilDestroyed(this)).subscribe(() => {
            this.reloadCurrentUserState();
        });
        this.inactivityService.warning$.pipe(untilDestroyed(this)).subscribe(() => {
            this.inactivityLogoutModal.open();
        });
        this.inactivityService.warningCleared$.pipe(untilDestroyed(this)).subscribe(() => {
            if (this.inactivityLogoutModal?.modal?.isOpen()) {
                this.inactivityLogoutModal.close(false);
            }
        });
        this.updateLayoutState(this.router.url);
        this.router.events.subscribe((res) => {
            if (res instanceof NavigationStart) {
                this.updateLayoutState(res.url);
            }
        });
    }

    get hasResolutionLimit() {
        return !this.isMobileEnabled && this.screenWidth < 554;
    }

    onLogout() {
        this._logger.info('onLogout:: ');

        // Check if user authenticated via legacy auth (read before clearing storage)
        const isLegacyAuth = this.authService.isLegacyAuth();
        const hasPreservedTechnicalSession = this.authService.hasPreservedTechnicalUserSession();
        const finalizeLogout = () => {
            if (hasPreservedTechnicalSession) {
                const returnUrl = this.authService.restoreTechnicalUserSession();
                window.location.replace(returnUrl || '/dashboard');
                return;
            }

            // Clear storage after server is notified
            this.authService.clearAuthStorage();

            if (environment.msalEnable && this.msalService && !isLegacyAuth) {
                // Clear MSAL active account before logout
                this.msalService.instance.setActiveAccount(null);

                // Use MSAL logout redirect
                this.msalService.logoutRedirect({
                    postLogoutRedirectUri: this.authService.getMsalRedirectUri()
                });
            } else {
                // Legacy logout - navigate to auth
                this.router.navigate(['auth']);
            }
        };

        // Always notify server about logout
        this.authService.logout().subscribe({
            next: (data) => {
                this._logger.info('onLogout:: Server logout successful.', data);
            },
            error: (error) => {
                this._logger.error('onLogout:: Server logout failed.', error);
                finalizeLogout();
            },
            complete: () => {
                finalizeLogout();
            }
        });
    }

    onLanguageChange(language) {
        this.translateService.use(language);
        localStorage.setItem(this.languageKey, language);
    }

    onSelectedPlant(selectedPlants: any[]) {
        localStorage.setItem('selectedPlants', selectedPlants.toString());
        this.userService.setSelectedPlants(+this.currentUser.userId, selectedPlants).subscribe(() => {
                this.notificationService.emitSelectedPlantsChanged();
            }
        );
        this.selectedPlants = selectedPlants;
    }

    onRoleChange(roleOption: AssignedRoleOption) {
        if (!roleOption || !this.currentUser || roleOption.name === this.currentUser.role || this.isRoleSwitchInProgress) {
            return;
        }

        this.isRoleSwitchInProgress = true;
        this.authService.switchRole(roleOption.id).pipe(
            finalize(() => this.isRoleSwitchInProgress = false)
        ).subscribe(
            () => {
                this.reloadCurrentUserState();
                this.navigateToDefaultRoute();
            },
            (error) => {
                this._logger.error('onRoleChange:: Error while switching role', error);
                this.syncSelectedRole();
            }
        );
    }

    ngOnDestroy(): void {
        this.inactivityService.stop();
        if (this.selectedPlantsSubscription) {
            this.selectedPlantsSubscription.unsubscribe();
        }
    }


    private shouldHideHeaderShadow(url: string) {
        this.hideHeaderShadow = url.startsWith('/tool-management/');
    }

    private reloadCurrentUserState() {
        this.currentUser = this.authService.getUserDetails();
        this.updateInactivityTracking();
        this.reloadPlantState();
        this.reloadServiceOperations();
        this.loadAssignedRoles();
    }

    private reloadPlantState() {
        if (this.selectedPlantsSubscription) {
            this.selectedPlantsSubscription.unsubscribe();
            this.selectedPlantsSubscription = null;
        }

        if (!this.currentUser || !this.plantDropdownRoles.find(role => role === this.currentUser.role)) {
            this.selectedPlants = [];
            this.allPlants$ = null;
            this.selectedPlants$ = null;
            localStorage.setItem('selectedPlants', '');
            return;
        }

        this.allPlants$ = this.plantService.getAllPlants();
        this.selectedPlants$ = this.userService.getSelectedPlants(+this.currentUser.userId);
        this.selectedPlantsSubscription = this.selectedPlants$.subscribe(plants => {
                this.selectedPlants = plants;
                localStorage.setItem('selectedPlants', plants.toString());
                this.notificationService.emitSelectedPlantsReceived();
            }
        );
    }

    private loadAssignedRoles() {
        if (!this.currentUser) {
            this.availableRoles = [];
            this.selectedRoleId = null;
            return;
        }

        this.authService.getAssignedRoles().subscribe(
            (roles: any[]) => {
                this.availableRoles = (roles || []).map(role => ({
                    id: role.id,
                    name: role.name,
                    label: this.formatRoleLabel(role.name)
                }));
                this.syncSelectedRole();
            },
            (error) => {
                this._logger.error('loadAssignedRoles:: Error while loading assigned roles', error);
                this.availableRoles = [];
                this.selectedRoleId = null;
            }
        );
    }

    private syncSelectedRole() {
        if (!this.currentUser) {
            this.selectedRoleId = null;
            return;
        }

        const currentRole = this.availableRoles.find(role => role.name === this.currentUser.role);
        this.selectedRoleId = currentRole ? currentRole.id : null;
    }

    private formatRoleLabel(role: string): string {
        return role ? role.replace(/_/g, ' ') : '';
    }

    private navigateToDefaultRoute() {
        this.navigationService.navigateToDefaultRoute(this.currentUser);
    }

    private updateLayoutState(url: string) {
        this.shouldHideHeaderShadow(url);
        this.isSelectionPage = url.startsWith('/section-selection');
        this.hideSidebarForSelection = this.isSelectionPage || url.startsWith('/dashboard') || url.startsWith('/scan-badge');
        this.hideToolbarForSelection = this.isSelectionPage;
    }

    private reloadServiceOperations() {
        if (!this.currentUser ||
            !Array.isArray(this.currentUser.permissions) ||
            this.currentUser.permissions.indexOf('PROCESS_TICKETS') === -1) {
            this.serviceOperations = [];
            return;
        }

        this.operationTypeService.getAllOperationTypes().subscribe(
            (data: any) => {
                this.serviceOperations = data && data.operationTypes ? data.operationTypes : [];
            },
            (error: any) => {
                this._logger.error('reloadServiceOperations:: API returned error', error);
                this.serviceOperations = [];
            }
        );
    }

    private updateInactivityTracking() {
        if (this.isBadgeAuthenticatedSession()) {
           this.inactivityService.start(environment.badgeInactivityTimeoutSeconds, this.badgeLogoutCountdownSeconds);
            return;
        }

        this.inactivityService.stop();
    }

    private isBadgeAuthenticatedSession(): boolean {
        return this.currentUser?.authenticationType === AuthenticationType.Badge;
    }

    onContinueAfterInactivityWarning() {
        this.inactivityService.continueSession();
    }

}
